import React, { useEffect, Suspense, lazy, useCallback, memo, useMemo } from "react";
import { 
  createBrowserRouter, 
  RouterProvider, 
  createRoutesFromElements, 
  Route, 
  Outlet, 
  useRouteError,
  useNavigate,
  useLocation,
  Navigate,
  useParams
} from "react-router-dom";
import { ErrorBoundary } from 'react-error-boundary';

// Import Analytics Service
import { AnalyticsService } from "./Services/Firebase/FirebaseModules/firebaseAnalyticsService";

// Import Debug Utility
import debug from "./utils/debug";

// Import Popup Error Handler
import { initializePopupErrorHandler } from "./Services/Firebase/FirebaseModules/popupErrorHandler";

// Layout Components
import HeaderManager from "./Components/Headers/HeaderManager";
import Footer from "./Components/Footer/FooterConfig";
import UniversalProfileLayout from "./Components/Layout/UniversalProfileLayout";

// Global Loading Components
import LoadingOverlay from "./Components/GlobalLoading/LoadingOverlay";
import { useLoading } from "./Components/GlobalLoading/LoadingContext";
import { useAuth } from "./Components/Auth/AuthContext";

// Staging Gate Component
import StagingGate from "./Components/StagingGate";
import ChatBot from "./Components/ChatBot/GigBot";

// Providers
import { AuthProvider } from "./Components/Auth/AuthContext";
import { LoadingProvider } from "./Components/GlobalLoading/LoadingContext";
import { UserProfileProvider } from "./Components/Auth/UserProfileContext";

// Eagerly load critical page components
import LandingPage from "./Pages/LandingPage/LandingPage";
import Register from "./Pages/RegisterLogin/Register";
import Login from "./Pages/RegisterLogin/Login";
import ForgotPassword from "./Pages/RegisterLogin/ForgotPassword";
import ProtectedRoute from "./Components/Auth/ProtectedRoute";
import EmailVerification from "./Components/Auth/EmailVerification";
import EmailVerified from "./Pages/EmailVerified";
import SecurityNotification from "./Pages/SecurityNotification";
import BookingLoadingStatePopupDev from "./BookingFlow/components/BookingLoadingStatePopupDev";
// Removed AgreementTester import - files deleted

// Development-only imports
import TestFloatingTab from "./Pages/TestFloatingTab";

// Lazily load non-critical page components
const AboutUs = lazy(() => import("./Pages/AboutUs"));
const ForTalent = lazy(() => import("./Pages/ForTalent"));
const ForOrganisers = lazy(() => import("./Pages/ForOrganisers"));
const Explore = lazy(() => import("./Pages/Explore"));
const HowItWorks = lazy(() => import("./Pages/NewHowItWorks"));
const PricingPayments = lazy(() => import("./Pages/PricingPayments"));
const ExploreEvents = lazy(() => import("./Pages/ExploreEvents/ExploreEvents"));
const CommunityStandards = lazy(() => import("./Pages/Legal/CommunityStandards"));
const PerformanceAgreement = lazy(() => import("./Pages/Legal/PerformanceAgreement"));
const PrivacyPolicy = lazy(() => import("./Pages/Legal/PrivacyPolicy"));
const ServiceTerms = lazy(() => import("./Pages/Legal/ServiceTerms"));
const TalentBenefits = lazy(() => import("./Pages/TalentBenefits"));
const OrganiserBenefits = lazy(() => import("./Pages/OrganiserBenefits"));
const SuccessStories = lazy(() => import("./Pages/SuccessStories"));
const ContactUs = lazy(() => import("./Pages/ContactUs.js"));
const FAQ = lazy(() => import("./Pages/FAQ.js"));

// Unified Notifications Component
const UnifiedNotifications = lazy(() => import("./Pages/UnifiedNotifications"));

// Browse Talent Components
const BrowseTalent = lazy(() => import("./Pages/BrowseTalent"));
const BrowseTalentV2 = lazy(() => import("./Pages/BrowseTalentV2"));

// Talent Registration Flow
const TalentBlueprint = lazy(() => import("./UserRegistration/TalentReg/TalentBlueprint"));
const TalentStudio = lazy(() => import("./UserRegistration/TalentReg/TalentStudio"));
const PerformanceHub = lazy(() => import("./UserRegistration/TalentReg/PerformanceHub"));
const TalentCard = lazy(() => import("./UserRegistration/TalentReg/TalentCard"));
const ComplianceVault = lazy(() => import("./UserRegistration/TalentReg/ComplianceVault"));
const PricingPortfolio = lazy(() => import("./UserRegistration/TalentReg/PricingPortfolio"));

// Talent Dashboard and Profile Components
const TalentProfile = lazy(() => import("./TalentProfiles/TalentPublicProfiles2/UnifiedPublicProfile.js"));
// Legacy components removed - TalentPublicProfiles folder deleted, now using TalentPublicProfiles2/UnifiedPublicProfile
// const ActorProfile = lazy(() => import("./TalentProfiles/TalentPublicProfiles/ActorEPKlayout.js"));
// const LegacyTalentProfile = lazy(() => import("./TalentProfiles/TalentPublicProfiles/ProfileTemplateRouter.js")); 
const TalentDashboard = lazy(() => import("./TalentProfiles/TalentDashboard"));
const TalentGigApplications = lazy(() => import("./TalentProfiles/TalentGigApplications"));
const TalentBookingManagement = lazy(() => import("./TalentProfiles/TalentBookingManagement"));
const TalentContractsLocker = lazy(() => import("./TalentProfiles/TalentContractsLocker"));
const TalentInbox = lazy(() => import("./TalentProfiles/OptimisedTalentInbox"));
const TalentAnalytics = lazy(() => import("./TalentProfiles/TalentAnalytics"));
const TalentSettings = lazy(() => import("./TalentProfiles/TalentSettings"));
const TalentNews = lazy(() => import("./TalentProfiles/TalentNews"));
const TalentRiders = lazy(() => import("./TalentProfiles/TalentProfileComponents/TalentRiders"));
const LoggedOut = lazy(() => import("./TalentProfiles/Logout"));

// Organiser Dashboard and Profile Components
const OrganiserBlueprint = lazy(() => import("./UserRegistration/OrganiserReg/OrganiserBlueprint"));
const OrganiserDashboard = lazy(() => import("./OrganiserProfiles/OrganiserDashboard"));
const OrganiserBookingManagement = lazy(() => import("./OrganiserProfiles/OrganiserBookingManagement"));
const OrganiserContractsLocker = lazy(() => import("./OrganiserProfiles/OrganiserContractsLocker"));
const OrganiserInbox = lazy(() => import("./OrganiserProfiles/OptimisedOrganiserInbox"));
const OrganiserNews = lazy(() => import("./OrganiserProfiles/OrganiserNews"));
const OrganiserAnalytics = lazy(() => import("./OrganiserProfiles/OrganiserAnalytics"));
const OrganiserSettings = lazy(() => import("./OrganiserProfiles/OrganiserSettings"));
const OrganiserEvent = lazy(() => import("./OrganiserProfiles/OrganiserEvent"));
const OrganiserEventManagement = lazy(() => import("./OrganiserProfiles/OrganiserEventManagement"));

// Admin Imports
const AdminDashboard = lazy(() => import("./Admin/AdminPages/AdminDashboard"));
const AdminAnalytics = lazy(() => import("./Admin/AdminPages/AdminAnalytics"));
const AdminUserManagement = lazy(() => import("./Admin/AdminPages/AdminUserManagement"));
const AdminBookingManagement = lazy(() => import("./Admin/AdminPages/AdminBookingManagement"));
const AdminFlowMonitor = lazy(() => import("./Admin/AdminPages/AdminFlowMonitor"));
const AdminContentManagement = lazy(() => import("./Admin/AdminPages/AdminContentManagement"));
const AdminSystemSettings = lazy(() => import("./Admin/AdminPages/AdminSystemSettings"));
const AdminAuditLogs = lazy(() => import("./Admin/AdminPages/AdminAuditLogs"));
const AdminAuthentication = lazy(() => import("./Admin/AdminAuthentication"));
const AdminBookingStatus = lazy(() => import("./Admin/AdminPages/AdminSystemSettings"));
const EscrowChecker = lazy(() => import("./Admin/AdminComponents/EscrowChecker"));
const TradeSafeTestTools = lazy(() => import("./Admin/AdminComponents/TradeSafeTestTools"));
const CalendarMigrationTool = lazy(() => import("./Components/Migration/CalendarMigrationTool"));
const CalendarPageWrapper = lazy(() => import("./Components/Calendar/core/CalendarPageWrapper"));
const GalleryTestPage = lazy(() => import("./Admin/AdminPages/GalleryTestPage"));
const AdminProfileTemplates = lazy(() => import("./Admin/AdminPages/AdminProfileTemplates"));
const ProfileDesignSandbox = lazy(() => import("./Admin/AdminPages/ProfileDesignSandbox"));
const AdminTalentList = lazy(() => import("./Admin/AdminPages/AdminTalentList"));
const AdminOrganiserList = lazy(() => import("./Admin/AdminPages/AdminOrganiserList"));
const AdminUserDetail = lazy(() => import("./Admin/AdminPages/AdminUserDetail"));
const AdminComingSoon = lazy(() => import("./Admin/AdminPages/AdminComingSoon"));

// Profile Template Imports - Removed with TalentPublicProfiles folder
// Use TalentPublicProfiles2/UnifiedPublicProfile instead
// const SpeakerProfile = lazy(() => import("./TalentProfiles/TalentPublicProfiles/SpeakerProfile"));

// Booking Flow Components
const BookingRequestForm = lazy(() => import("./BookingFlow/components/RequestFlow/BookingRequestForm"));
const BookingConfirmation = lazy(() => import("./BookingFlow/components/RequestFlow/BookingConfirmation"));
const RequestSummary = lazy(() => import("./BookingFlow/components/RequestFlow/RequestSummary"));
const OrganiserSigningSuccess = lazy(() => import("./BookingFlow/components/OrganiserSigningSuccess"));
const TalentSigningSuccess = lazy(() => import("./BookingFlow/components/TalentSigningSuccess"));
const LegacySigningRedirect = lazy(() => import("./BookingFlow/components/LegacySigningRedirect"));
const EscrowInitiation = lazy(() => import("./BookingFlow/components/PaymentFlow/EscrowInitiation"));
const PaymentStatus = lazy(() => import("./BookingFlow/components/PaymentFlow/PaymentStatus"));
const PaymentSuccess = lazy(() => import("./BookingFlow/components/PaymentFlow/PaymentSuccess"));
const PaymentFailure = lazy(() => import("./BookingFlow/components/PaymentFlow/PaymentFailure"));

// Newly Added Booking Workflow Components
const RiderPreviewPage = lazy(() => import("./BookingFlow/components/ContractFlow/Riders/RiderPreviewPage"));
const TechnicalRiderPreview = lazy(() => import("./BookingFlow/components/ContractFlow/Riders/TechnicalRiderPreview"));
const LogisticsRiderPreview = lazy(() => import("./BookingFlow/components/ContractFlow/Riders/LogisticsHospitalityRiderPreview"));

// Rating and Review Components - NEW
const RatingReviewForm = lazy(() => import("./BookingFlow/components/ReviewFlow/RatingReviewForm"));
const TalentRating = lazy(() => import("./TalentProfiles/TalentRating"));
const OrganiserRating = lazy(() => import("./OrganiserProfiles/OrganiserRating.js"));

// Dispute Flow Component - NEW
const DisputeFlow = lazy(() => import("./BookingFlow/components/DisputeFlow/DisputeFlow"));

// Development Components
const TestPage = lazy(() => import("./testpage"));
const TestingDashboard = lazy(() => import("./Pages/TestingDashboard"));
const EnhancedTestingDashboard = lazy(() => import("./Pages/EnhancedTestingDashboard"));
const ComponentsShowcase = lazy(() => import("./Pages/ComponentsShowcase"));

// Auth callback pages
const GoogleCallback = lazy(() => import("./Pages/Auth/GoogleCallback"));

// Redirect legacy Tier-2 routes into the combined Talent Card page (preserves uid + tab)
const RedirectToCard = ({ tab }) => {
  const { uid } = useParams();
  return <Navigate to={`/talent-card/${uid}?tab=${tab}`} replace />;
};

// Common suspense fallback component
const SuspenseFallback = memo(() => (
  <div className="loading-placeholder" style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    padding: 'clamp(1rem, 5vh, 2rem)',
    minHeight: 'min(60vh, 400px)'
  }}>
    <div style={{ 
      textAlign: 'center', 
      fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
      color: '#144874'
    }}>
      Loading...
    </div>
  </div>
));

SuspenseFallback.displayName = 'SuspenseFallback';

// Detect lazy-chunk load failures (stale build in the browser after a deploy —
// the old runtime requests a chunk hash that no longer exists on hosting).
const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' ||
  /Loading (CSS )?chunk [\d]+ failed/i.test(error?.message || '') ||
  /Failed to fetch dynamically imported module/i.test(error?.message || '');

// Error fallback component for the ErrorBoundary
const ErrorFallbackComponent = memo(({ error, resetErrorBoundary }) => {
  // A chunk failure means the user's cached build is stale: reload once to pull
  // the fresh index.html + chunks. The sessionStorage guard prevents a reload
  // loop if the failure persists for another reason.
  useEffect(() => {
    if (isChunkLoadError(error)) {
      const lastReload = sessionStorage.getItem('chunkErrorReloadAt');
      if (!lastReload || Date.now() - Number(lastReload) > 30000) {
        sessionStorage.setItem('chunkErrorReloadAt', String(Date.now()));
        window.location.reload();
      }
    }
  }, [error]);

  // Log error with analytics service
  const logError = useCallback(() => {
    // Use Promise to make analytics call non-blocking
    Promise.resolve().then(() => {
      if (AnalyticsService && typeof AnalyticsService.errorTracking?.logError === 'function') {
        AnalyticsService.errorTracking.logError('route_rendering_error', {
          error: error.message,
          stack: error.stack,
          location: window.location.pathname,
          timestamp: new Date().toISOString()
        });
      }
    });
    
    debug.error('route_rendering_error', 'Error occurred', error);
  }, [error]);
  
  useEffect(() => {
    logError();
  }, [logError]);

  return (
    <div 
      className="error-container"
      style={{ 
        padding: 'clamp(1rem, 5vw, 2rem)', 
        textAlign: 'center', 
        backgroundColor: '#f8d7da', 
        color: '#721c24',
        maxWidth: '90%',
        margin: '0 auto',
        borderRadius: '0.5rem'
      }}
    >
      <h2 style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}>Oops! Something went wrong</h2>
      <p style={{ fontSize: 'clamp(1rem, 3vw, 1.2rem)' }}>We encountered an unexpected error while loading this page.</p>
      <button 
        onClick={resetErrorBoundary}
        style={{
          backgroundColor: '#01a2f1',
          color: 'white',
          border: 'none',
          padding: 'clamp(0.5rem, 2vw, 0.75rem) clamp(1rem, 4vw, 1.5rem)',
          borderRadius: '5px',
          cursor: 'pointer',
          fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
          minWidth: '44px',
          minHeight: '44px',
          margin: 'clamp(0.5rem, 3vw, 1rem) 0'
        }}
      >
        Try Again
      </button>
      <details style={{ 
        whiteSpace: 'pre-wrap', 
        marginTop: 'clamp(0.75rem, 3vw, 1.5rem)',
        textAlign: 'left',
        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
        maxWidth: '100%',
        overflow: 'auto'
      }}>
        <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Error Details</summary>
        {error.message}
      </details>
    </div>
  );
});

ErrorFallbackComponent.displayName = 'ErrorFallbackComponent';

// Router error component
const RouterErrorBoundary = memo(() => {
  const error = useRouteError();
  const navigate = useNavigate();
  
  // Log error with analytics service
  const logError = useCallback(() => {
    // Use Promise to make analytics call non-blocking
    Promise.resolve().then(() => {
      if (AnalyticsService && typeof AnalyticsService.errorTracking?.logError === 'function') {
        AnalyticsService.errorTracking.logError('router_error', {
          error: error?.message || 'Unknown router error',
          stack: error?.stack,
          location: window.location.pathname,
          timestamp: new Date().toISOString()
        });
      }
    });
    
    debug.error('router_error', 'Error occurred', error);
  }, [error]);
  
  useEffect(() => {
    logError();
  }, [logError]);
  
  return (
    <div 
      className="router-error-container"
      style={{ 
        padding: 'clamp(1rem, 5vw, 2rem)', 
        textAlign: 'center', 
        backgroundColor: '#f8d7da', 
        color: '#721c24',
        maxWidth: 'min(90vw, 600px)',
        margin: 'clamp(2rem, 8vh, 4rem) auto',
        borderRadius: '0.5rem'
      }}
    >
      <h2 style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}>Navigation Error</h2>
      <p style={{ fontSize: 'clamp(1rem, 3vw, 1.2rem)' }}>We encountered an error while navigating to this page.</p>
      <button 
        onClick={() => navigate('/')}
        style={{
          backgroundColor: '#01a2f1',
          color: 'white',
          border: 'none',
          padding: 'clamp(0.5rem, 2vw, 0.75rem) clamp(1rem, 4vw, 1.5rem)',
          borderRadius: '5px',
          cursor: 'pointer',
          fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
          minWidth: '44px',
          minHeight: '44px',
          margin: 'clamp(0.5rem, 3vw, 1rem) 0',
          touchAction: 'manipulation'
        }}
      >
        Go to Home Page
      </button>
      <details style={{ 
        whiteSpace: 'pre-wrap', 
        marginTop: 'clamp(0.75rem, 3vw, 1.5rem)',
        textAlign: 'left',
        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
        maxWidth: '100%',
        overflow: 'auto'
      }}>
        <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Error Details</summary>
        {error?.message || 'Unknown router error'}
      </details>
    </div>
  );
});

RouterErrorBoundary.displayName = 'RouterErrorBoundary';

// Booking Management Wrapper to route to the correct component based on user type
const BookingManagementWrapper = memo(() => {
  const { userType } = useAuth();
  
  return userType === "Organiser" 
    ? <OrganiserBookingManagement /> 
    : <TalentBookingManagement />;
});

BookingManagementWrapper.displayName = 'BookingManagementWrapper';

// Layout component that adds header and footer
const AppLayout = () => {
    const { isLoading } = useLoading();
    const { currentUser } = useAuth();
    const location = useLocation();
    const [isDesktop, setIsDesktop] = React.useState(window.innerWidth >= 768);
    
    // Track window resize for ChatBot rendering
    React.useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth >= 768);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Define routes that don't need header 
    const noHeaderRoutes = ["/lp2"];
    
    // Define routes that don't need footer
    const noFooterRoutes = [
        "/lp2",
        // Auth pages
        "/verify-email",
        "/email-verified",
        "/register",
        "/login",
        // Profile pages
        "/talent-profile",
        "/actor-profile",
        // Talent dashboard pages
        "/talent-dashboard",
        "/talent-applications",
        "/talent-studio",
        "/talent-contracts-locker",
        "/talent-inbox",
        "/talent-analytics",
        "/talent-news",
        "/talent-settings",
        "/talent-rating",
        "/talent-blueprint",
        "/performance-hub",
        "/pricing-portfolio",
        "/talent-calendar",
        "/talent-riders",
        // Organiser dashboard pages
        "/organiser-dashboard",
        "/organiser-contracts-locker",
        "/organiser-inbox",
        "/organiser-analytics",
        "/organiser-news",
        "/organiser-settings",
        "/organiser-rating",
        "/organiser-blueprint",
        "/organiser-calendar",
        "/booking-management",
        "/browse-talent",
        // Admin dashboard pages
        "/admin-dashboard",
        "/admin-analytics",
        "/admin-users",
        "/admin-bookings",
        "/admin-content",
        "/admin-settings",
        "/admin-audit",
        "/admin-permissions",
        "/admin-login",
        // Test pages
    ];
    
    // Check if current path should hide header and footer
    const shouldShowHeader = !noHeaderRoutes.includes(location.pathname.toLowerCase());
    const shouldShowFooter = !noFooterRoutes.some(route => 
        location.pathname.toLowerCase().startsWith(route)
    );

    // Setup global error handling
    useEffect(() => {
      // Initialize popup error handler to suppress COOP errors
      const cleanupPopupHandler = initializePopupErrorHandler();
      
      // Setup global error handler
      const originalConsoleError = console.error;
      console.error = (...args) => {
        // Log to analytics service if available - make non-blocking
        Promise.resolve().then(() => {
          if (AnalyticsService && typeof AnalyticsService.errorTracking?.logError === 'function') {
            const errorMessage = args.map(arg => {
              if (typeof arg === 'object') {
                try {
                  // Try to stringify, but handle circular references
                  return JSON.stringify(arg);
                } catch (e) {
                  // If circular reference, just use the message
                  return arg.message || arg.toString() || '[Circular Object]';
                }
              }
              return String(arg);
            }).join(' ');

            // Break the error-logging feedback loop: errorTracking.logError writes
            // to server-only analytics collections, so a permission-denied (or the
            // logger's own failure) would itself console.error and re-enter here,
            // amplifying into an endless flood. Never re-log those to analytics.
            const isLoggingLoopError = /Missing or insufficient permissions|Critical error logging failed|permission-denied/i.test(errorMessage);
            if (isLoggingLoopError) {
              return;
            }

            AnalyticsService.errorTracking.logError('global_console_error', {
              message: errorMessage,
              location: window.location.pathname,
              timestamp: new Date().toISOString()
            });
          }
        });
        
        // Log with our debug utility
        debug.error('ConsoleError', args[0], args.slice(1));
        
        // Call original console.error
        originalConsoleError.apply(console, args);
      };

      // Setup global unhandled promise rejection handler
      const handlePromiseRejection = (event) => {
        debug.error('UnhandledPromise', 'Unhandled Promise Rejection', event.reason);
        console.error('Unhandled Promise Rejection:', event.reason);
      };
      window.addEventListener('unhandledrejection', handlePromiseRejection);

      // Log app initialization
      debug.log('App', 'Application initialized');

      // TradeSafe allocation release scheduling is now handled by Cloud Functions
      // See: functions/scheduledFunctions/allocationReleaseScheduler.js
      // This runs every 15 minutes on the server side with proper permissions

      // Cleanup
      return () => {
        console.error = originalConsoleError;
        window.removeEventListener('unhandledrejection', handlePromiseRejection);
        cleanupPopupHandler();
        
        
        debug.log('App', 'Application cleanup');
      };
    }, []);

    // Track route changes and performance - memoized
    const trackRouteChange = useCallback(() => {
      const startTime = performance.now();

      // Debug route change
      debug.router('Navigation', 'Route changed', { 
        path: location.pathname,
        search: location.search
      });

      // Track screen view - make non-blocking
      Promise.resolve().then(() => {
        AnalyticsService.trackScreenView(location.pathname, {
          path: location.pathname,
          search: location.search
        });
      });

      // Performance logging
      const endTime = performance.now();
      
      // Make performance tracking non-blocking
      Promise.resolve().then(() => {
        AnalyticsService.performanceTracking.logPageLoad(
          location.pathname, 
          endTime - startTime
        );
      });

      // Debug performance
      debug.log('Performance', `Page loaded in ${(endTime - startTime).toFixed(2)}ms`, {
        path: location.pathname
      });

      // Optional: Log route access for authentication tracking - make non-blocking
      if (currentUser) {
        Promise.resolve().then(() => {
          AnalyticsService.userEngagement.logFeatureUsage(
            currentUser.uid, 
            `route_access:${location.pathname}`
          );
        });
        
        debug.auth('User', 'Authenticated route access', {
          uid: currentUser.uid,
          path: location.pathname
        });
      }
    }, [location, currentUser]);

    // Effect for route tracking
    useEffect(() => {
      trackRouteChange();
    }, [trackRouteChange]);

    return (
        <ErrorBoundary FallbackComponent={ErrorFallbackComponent}>
            {isLoading && <LoadingOverlay />}
            <div className="app-container">
                {shouldShowHeader && <HeaderManager />}
                <main className="main-content">
                    <Suspense fallback={<SuspenseFallback />}>
                      <Outlet />
                    </Suspense>
                </main>
                
                {/* Show ChatBot on desktop only (mobile handled by header) */}
                {isDesktop && <ChatBot />}
                
                {/* Only show Footer on routes that need it */}
                {shouldShowFooter && <Footer />}
            </div>
        </ErrorBoundary>
    );
};

// Define protected route wrapper 
const ProtectedRouteWrapper = () => {
  return (
    <ProtectedRoute>
      <Outlet />
    </ProtectedRoute>
  );
};

// We need to wrap the router creation in App to ensure providers are available
const App = () => {
  // Define the routes - memoized to prevent recreating on rerenders
  const routes = useMemo(() => createRoutesFromElements(
    <Route path="/" element={<AppLayout />} errorElement={<RouterErrorBoundary />}>
      {/* Public Routes */}
      <Route index element={<LandingPage />} />
      <Route path="register" element={<Register />} />
      <Route path="login" element={<Login />} />
      <Route path="forgot-password" element={<ForgotPassword />} />
      <Route path="our-story" element={<AboutUs />} />
      <Route path="explore" element={<Explore />} />
      <Route path="for-talent" element={<ForTalent />} />
      <Route path="for-organisers" element={<ForOrganisers />} />
      <Route path="how-it-works" element={<HowItWorks />} />
      <Route path="pricing-payments" element={<PricingPayments />} />
      <Route path="browse-talent" element={<BrowseTalent />} />
      <Route path="browse-talent-v2" element={<BrowseTalentV2 />} />
      <Route path="explore-events" element={<ExploreEvents />} />
      <Route path="verify-email" element={<EmailVerification />} />
      <Route path="email-verified" element={<EmailVerified />} />
      <Route path="security-notification" element={<SecurityNotification />} />
      <Route path="auth/google/callback" element={<GoogleCallback />} />
      <Route path="service-terms" element={<ServiceTerms />} />
      <Route path="community-standards" element={<CommunityStandards />} />
      <Route path="privacy-policy" element={<PrivacyPolicy />} />
      <Route path="performance-agreement" element={<PerformanceAgreement />} />
      <Route path="logged-out" element={<LoggedOut />} />
      <Route path="talent-profile/:uid" element={<TalentProfile />} />
      <Route path="actor-profile/:uid" element={<TalentProfile />} />
      {/* Legacy profile router for fallback if needed */}
      {process.env.NODE_ENV === 'development' && (
        <Route path="talent-profile-legacy/:uid" element={<TalentProfile />} />
      )}
      <Route path="talent-benefits" element={<TalentBenefits />} />
      <Route path="organiser-benefits" element={<OrganiserBenefits />} />
      <Route path="success-stories" element={<SuccessStories />} />
      <Route path="success-stories/:type" element={<SuccessStories />} />
      <Route path="contact" element={<ContactUs />} />
      <Route path="faq" element={<FAQ />} />

      {/* Admin Routes */}
      <Route path="admin-login" element={<AdminAuthentication />} />
      {/* All admin pages are gated behind ProtectedRouteWrapper, which enforces the
          isAdmin check in ProtectedRoute. Pages render via that wrapper's <Outlet/>. */}
      <Route element={<ProtectedRouteWrapper />}>
        <Route path="admin-dashboard" element={<AdminDashboard />} />
        <Route path="admin-analytics" element={<AdminAnalytics />} />
        <Route path="admin-users" element={<AdminUserManagement />} />
        <Route path="admin-talent" element={<AdminTalentList />} />
        <Route path="admin-organisers" element={<AdminOrganiserList />} />
        <Route path="admin-user/:uid" element={<AdminUserDetail />} />
        <Route path="admin-bookings" element={<AdminBookingManagement />} />
        <Route path="admin-flow-monitor" element={<AdminFlowMonitor />} />
        <Route path="admin-content" element={<AdminContentManagement />} />
        <Route path="admin-settings" element={<AdminSystemSettings />} />
        <Route path="admin-audit-logs" element={<AdminAuditLogs />} />
        <Route path="admin-booking-status" element={<AdminBookingStatus />} />
        <Route path="admin-calendar-migration" element={<CalendarMigrationTool />} />
        <Route path="calendar-migration-test" element={<CalendarMigrationTool />} />
        <Route path="admin-escrow-checker" element={<EscrowChecker />} />
        <Route path="admin-tradesafe-test" element={<TradeSafeTestTools />} />
        <Route path="admin-gallery-test" element={<GalleryTestPage />} />
        <Route path="admin-profile-templates" element={<AdminProfileTemplates />} />
        <Route path="admin-profile-sandbox" element={<ProfileDesignSandbox />} />
        <Route path="admin-profile-template/speaker-profile" element={<TalentProfile />} />
        {/* Stubbed sections (Coming Soon) — links exist in the sidebar */}
        <Route path="admin-financial" element={<AdminComingSoon title="Financial Reports" />} />
        <Route path="admin-news" element={<AdminComingSoon title="News Administration" />} />
        <Route path="admin-calendar" element={<AdminComingSoon title="Calendar" />} />
        <Route path="admin-contracts-vault" element={<AdminComingSoon title="Contracts Vault" />} />
        <Route path="admin-dev-mediarow" element={<AdminComingSoon title="Dev — MediaRow" />} />
      </Route>

      {/* Development Routes - only available in non-production */}
      {process.env.NODE_ENV !== 'production' && (
        <>
          <Route path="dev-booking-popup" element={<BookingLoadingStatePopupDev />} />
          <Route path="test-floating-tab" element={<TestFloatingTab />} />
        </>
      )}

      {/* Rider and Technical Documentation Routes */}
      <Route path="rider-preview/:uid" element={<RiderPreviewPage />} />
      <Route path="technical-rider-preview/:uid" element={<TechnicalRiderPreview />} />
      <Route path="logistics-rider-preview/:uid" element={<LogisticsRiderPreview />} />

      {/* Protected Routes */}
      <Route element={<ProtectedRouteWrapper />}>
        {/* Talent Routes */}
        <Route path="talent-blueprint/:uid" element={<RedirectToCard tab="blueprint" />} />
        <Route path="talent-dashboard/:uid" element={<TalentDashboard />} />
        <Route path="talent-applications/:uid" element={<TalentGigApplications />} />
        <Route path="talent-studio/:uid" element={<RedirectToCard tab="studio" />} />
        <Route path="performance-hub/:uid" element={<RedirectToCard tab="performance" />} />
        <Route path="talent-card/:uid" element={<TalentCard />} />
        <Route path="compliance-vault/:uid" element={<UniversalProfileLayout title="Compliance Vault" requireAuth={true}><ComplianceVault embedded={true} /></UniversalProfileLayout>} />
        <Route path="pricing-portfolio/:uid" element={<UniversalProfileLayout title="Pricing & Packages" requireAuth={true} allowedUserTypes={['Talent']}><PricingPortfolio embedded={true} /></UniversalProfileLayout>} />
        <Route path="talent-contracts-locker/:uid" element={<TalentContractsLocker />} />
        <Route path="talent-calendar/:uid" element={<CalendarPageWrapper userType="talent" />} />
        <Route path="talent-inbox/:uid" element={<TalentInbox />} />
        <Route path="talent-analytics/:uid" element={<TalentAnalytics />} />
        <Route path="talent-news/:uid" element={<TalentNews />} />
        <Route path="talent-settings/:uid" element={<TalentSettings />} />
        <Route path="talent-riders/:uid" element={<TalentRiders />} />
        <Route path="talent-rating/:uid" element={<TalentRating />} />
        <Route path="talent-notifications/:uid" element={<UnifiedNotifications />} />

        {/* Organiser Routes */}
        <Route path="organiser-blueprint/:uid" element={<OrganiserBlueprint />} />
        <Route path="organiser-dashboard/:uid" element={<OrganiserDashboard />} />
        <Route path="organiser-contracts-locker/:uid" element={<OrganiserContractsLocker />} />
        <Route path="organiser-inbox/:uid" element={<OrganiserInbox />} />
        <Route path="organiser-calendar/:uid" element={<CalendarPageWrapper userType="organiser" />} />
        <Route path="organiser-analytics/:uid" element={<OrganiserAnalytics />} />
        <Route path="organiser-news/:uid" element={<OrganiserNews />} />
        <Route path="browse-talent/:uid" element={<BrowseTalent />} />
        <Route path="organiser-settings/:uid" element={<OrganiserSettings />} />
        <Route path="organiser-rating/:uid" element={<OrganiserRating />} />
        <Route path="organiser-notifications/:uid" element={<UnifiedNotifications />} />
        <Route path="organiser-event/:uid" element={<OrganiserEvent />} />
        <Route path="organiser-event-management/:uid" element={<OrganiserEventManagement />} />

        {/* New Unified Booking Management Route */}
        <Route path="booking-management/:uid" element={<BookingManagementWrapper />} />
        
        {/* Signing Success Routes */}
        <Route path="organiser-signing-success/:bookingId" element={<OrganiserSigningSuccess />} />
        <Route path="talent-signing-success/:bookingId" element={<TalentSigningSuccess />} />
        
        {/* Legacy Signing Success Route - redirects old Signiflow URLs */}
        <Route path="booking-management" element={<LegacySigningRedirect />} />

        {/* Booking Routes */}
        <Route path="booking-confirmation/:bookingId" element={<BookingConfirmation />} />
        <Route path="booking-request/:uid" element={<BookingRequestForm />} />
        <Route path="request-summary/:bookingId" element={<RequestSummary />} />
        <Route path="payment/:bookingId" element={<EscrowInitiation />} />
        <Route path="payment-status/:bookingId" element={<PaymentStatus />} />
        <Route path="payment-success" element={<PaymentSuccess />} />
        <Route path="payment-failure" element={<PaymentFailure />} />
        
        {/* NEW: Rating and Review Form Route */}
        <Route path="rate-review/:bookingId" element={<RatingReviewForm />} />
        
        {/* NEW: Dispute Flow Route */}
        <Route path="dispute/:bookingId" element={<DisputeFlow />} />
      </Route>
      
      {/* Development Routes */}
      {process.env.NODE_ENV === "development" && (
        <>
          <Route path="/test" element={<TestPage />} />
          <Route path="/testing-dashboard" element={<TestingDashboard />} />
          <Route path="/enhanced-testing-dashboard" element={<EnhancedTestingDashboard />} />
          <Route path="/components" element={<ComponentsShowcase />} />
        </>
      )}
    </Route>
  ), []);

  // Create router with routes
  const router = useMemo(() => createBrowserRouter(routes), [routes]);

  return (
    <StagingGate>
      <AuthProvider>
        <LoadingProvider>
          <UserProfileProvider>
            <Suspense fallback={
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '100vh', 
                fontSize: 'clamp(1rem, 3vw, 1.25rem)',
                color: '#144874',
                padding: 'clamp(1rem, 5vw, 2rem)'
              }}>
                Loading application...
              </div>
            }>
              <RouterProvider router={router} />
            </Suspense>
          </UserProfileProvider>
        </LoadingProvider>
      </AuthProvider>
    </StagingGate>
  );
};

export default App;